/**
 * Infusion Order Detail Page
 * Shows infusion order details, status history, and allows state machine transitions.
 * Handles version field for optimistic locking.
 * 
 * Medical safety red line: Identity check is mandatory before infusion.
 * State machine: scheduled → identity_check_pending → ready_for_infusion → infusing → completed
 */
import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { useInfusionOrderDetail, useInfusionOrderTransition, type InfusionOrderView } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { canReadInfusion, canUpdateInfusion, canVerifyInfusionIdentity } from '@/lib/labPermissions';

type InfusionOrderStatus = 'scheduled' | 'identity_check_pending' | 'ready_for_infusion' | 'infusing' | 'completed' | 'aborted' | 'cancelled' | 'identity_check_failed';

// Valid state transitions per state-machines.md
// Infusion Orders: scheduled → identity_check_pending → ready_for_infusion → infusing → completed
const VALID_TRANSITIONS: Record<string, string[]> = {
  scheduled: ['identity_check_pending', 'cancelled'],
  identity_check_pending: ['ready_for_infusion', 'identity_check_failed', 'cancelled'],
  ready_for_infusion: ['infusing', 'aborted', 'cancelled'],
  infusing: ['completed', 'aborted'],
  completed: [],
  aborted: [],
  cancelled: [],
  identity_check_failed: ['cancelled'],
};

export default function InfusionOrderDetailPage() {
  const { infusionId } = useParams<{ infusionId: string }>();
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { fetch: fetchDetail, loading: detailLoading } = useInfusionOrderDetail(infusionId!);
  const { transition: transitionStatus, loading: transitionLoading } = useInfusionOrderTransition(infusionId!);

  const [order, setOrder] = useState<InfusionOrderView | null>(null);
  const [selectedTransition, setSelectedTransition] = useState<InfusionOrderStatus | ''>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canReadInfusion(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看输液单的权限</p>
        </Card>
      </div>
    );
  }

  // Load infusion order detail
  useEffect(() => {
    const loadOrder = async () => {
      try {
        const data = await fetchDetail();
        if (data) {
          setOrder(data);
        }
      } catch (err) {
        toast.error('加载输液单详情失败');
      }
    };

    if (infusionId) {
      loadOrder();
    }
  }, [infusionId, fetchDetail]);

  const handleTransition = async () => {
    if (!order || !selectedTransition) {
      toast.error('请选择转换状态');
      return;
    }

    if (!canUpdateInfusion(permissions)) {
      toast.error('您没有修改输液单状态的权限');
      return;
    }

    try {
      await transitionStatus(selectedTransition, order.version);
      toast.success('状态已更新');
      setSelectedTransition('');
      setConfirmDialogOpen(false);
      
      // Refetch order details to ensure consistency with backend
      const updatedData = await fetchDetail();
      if (updatedData) {
        setOrder(updatedData);
      }
    } catch (err: unknown) {
      // Backend rejection must be shown as-is
      toast.error(`状态转换失败: ${(err as Error)?.message || '未知错误'}`);
    }
  };

  const handleIdentityCheck = () => {
    if (!order) return;
    navigate(`/lab/infusion/${order.id}/verify`);
  };

  if (detailLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">加载中…</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">输液单不存在</h2>
        </Card>
      </div>
    );
  }

  const validTransitions = VALID_TRANSITIONS[order.status] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">输液单详情</h1>
        <p className="text-sm text-muted-foreground">输液单 ID: {order.id}</p>
      </div>

      {/* Medical Safety Warning */}
      {order.status === 'identity_check_pending' && (
        <Card className="border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-900">⚠️ 医疗安全红线</p>
          <p className="text-sm text-red-800 mt-2">
            输液前必须执行身份核对。执行人与审核人必须不同。核对失败将自动创建异常事件并阻止后续操作。
          </p>
        </Card>
      )}

      {/* Infusion Order Info */}
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-semibold text-muted-foreground">批次 ID</label>
            <p className="mt-1 text-sm font-mono">{order.cellBatchId}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">客户 ID</label>
            <p className="mt-1 text-sm font-mono">{order.customerId}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">当前状态</label>
            <p className="mt-1 text-sm">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(order.status)}`}>
                {getStatusLabel(order.status)}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">版本号</label>
            <p className="mt-1 text-sm font-mono">{order.version}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">创建时间</label>
            <p className="mt-1 text-sm">{new Date(order.createdAt).toLocaleString()}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">最后更新</label>
            <p className="mt-1 text-sm">{new Date(order.updatedAt).toLocaleString()}</p>
          </div>

        </div>
      </Card>

      {/* Identity Check Button */}
      {order.status === 'identity_check_pending' && canVerifyInfusionIdentity(permissions) && (
        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold">身份核对</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            请执行身份核对以确认患者身份，防止输液错误。
          </p>
          <Button
            onClick={handleIdentityCheck}
            className="w-full"
          >
            前往身份核对
          </Button>
        </Card>
      )}

      {/* Status Transition */}
      {validTransitions.length > 0 && canUpdateInfusion(permissions) && (
        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold">状态转换</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">选择目标状态</label>
              <select
                value={selectedTransition}
                onChange={(e) => setSelectedTransition((e.target.value || '') as InfusionOrderStatus | '')}
                className="mt-2 w-full rounded border border-input bg-background px-3 py-2 text-sm"
                disabled={transitionLoading}
              >
                <option value="">-- 选择状态 --</option>
                {validTransitions.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => setConfirmDialogOpen(true)}
              disabled={!selectedTransition || transitionLoading}
              className="w-full"
            >
              {transitionLoading ? '处理中…' : '确认转换'}
            </Button>
          </div>
        </Card>
      )}

      {/* Confirmation Dialog (simple version) */}
      {confirmDialogOpen && (
        <Card className="border-yellow-200 bg-yellow-50 p-6">
          <h4 className="font-semibold text-yellow-900">确认状态转换</h4>
          <p className="mt-2 text-sm text-yellow-800">
            您确定要将输液单从 <strong>{getStatusLabel(order.status)}</strong> 转换到{' '}
            <strong>{getStatusLabel(selectedTransition)}</strong> 吗？
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={handleTransition}
              disabled={transitionLoading}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {transitionLoading ? '处理中…' : '确认'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setSelectedTransition('');
              }}
              disabled={transitionLoading}
            >
              取消
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    scheduled: '已排期',
    identity_check_pending: '待身份核对',
    ready_for_infusion: '待输液',
    infusing: '输液中',
    completed: '已完成',
    aborted: '已中止',
    cancelled: '已取消',
    identity_check_failed: '核对失败',
  };
  return labels[status] || status;
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-900',
    identity_check_pending: 'bg-yellow-100 text-yellow-900',
    ready_for_infusion: 'bg-green-100 text-green-900',
    infusing: 'bg-purple-100 text-purple-900',
    completed: 'bg-green-100 text-green-900',
    aborted: 'bg-gray-100 text-gray-900',
    cancelled: 'bg-gray-100 text-gray-900',
    identity_check_failed: 'bg-red-100 text-red-900',
  };
  return colors[status] || 'bg-gray-100 text-gray-900';
}
